import { WebSourceOut } from '@/client';

/**
 * DuckDuckGo's icon service returns a 404 when it has no icon for a domain, so a miss is detectable
 * and the UI can fall back to its own glyph. Google's equivalent always answers with a generic
 * placeholder, which is indistinguishable from a hit.
 */
const FAVICON_SERVICE_ORIGIN = 'https://icons.duckduckgo.com/ip3';

/** Web sources come from a model, so a URL that `new URL()` rejects is a normal case, not a bug. */
const hostnameFromUrl = (url: string): string | null => {
	try {
		return new URL(url).hostname;
	} catch {
		return null;
	}
};

/**
 * `domain` reaches us from the model unvalidated, so it is encoded rather than interpolated raw —
 * a value carrying `/`, `?`, or `#` would otherwise reshape the request path.
 */
const faviconServiceUrl = (domain: string): string => `${FAVICON_SERVICE_ORIGIN}/${encodeURIComponent(domain)}.ico`;

/**
 * The single place that decides where a web source's icon comes from. When the backend starts
 * resolving and caching icons itself, this function returns `webSource.favicon_url` first and no
 * component changes.
 */
export const resolveFaviconUrl = (webSource: WebSourceOut): string | null => {
	const domain = webSource.domain?.trim() || hostnameFromUrl(webSource.url);
	return domain ? faviconServiceUrl(domain) : null;
};
